import { Head, router, usePage } from "@inertiajs/react";
import {
  ArrowRight,
  Check,
  Copy,
  Download,
  Eye,
  ImagePlus,
  Link2,
  Link2Off,
  Loader2,
  RefreshCcw,
  Settings,
  Upload,
} from "lucide-react";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";

import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type Format = "jpeg" | "png" | "webp" | "avif";
type AspectRatioValue = "original" | "4:3" | "3:2" | "16:9" | "1:1" | "1.905:1" | "custom";

const RESOLUTION_PRESETS = [
  { name: "OG Image (Facebook, X)", width: 1200, height: 630 },
  { name: "Instagram Square", width: 1080, height: 1080 },
  { name: "Instagram Portrait", width: 1080, height: 1350 },
  { name: "Instagram Story", width: 1080, height: 1920 },
  { name: "Pinterest Pin", width: 1000, height: 1500 },
  { name: "LinkedIn Post", width: 1200, height: 627 },
] as const;

const DEFAULT_PRESET = RESOLUTION_PRESETS[0];

const ASPECT_RATIOS: { label: string; value: AspectRatioValue; hint: string }[] = [
  { label: "Original", value: "original", hint: "Match source image" },
  { label: "1.91:1", value: "1.905:1", hint: "OG / social link preview" },
  { label: "16:9", value: "16:9", hint: "Widescreen" },
  { label: "4:3", value: "4:3", hint: "Classic" },
  { label: "3:2", value: "3:2", hint: "Photo" },
  { label: "1:1", value: "1:1", hint: "Square" },
  { label: "Custom", value: "custom", hint: "Width and height independent" },
];

const FIT_OPTIONS = [
  { value: "cover", label: "Cover", hint: "Fill the frame; crop overflow" },
  { value: "contain", label: "Contain", hint: "Fit inside; may letterbox" },
  { value: "fill", label: "Fill", hint: "Stretch to exact size" },
  { value: "scale-down", label: "Scale down", hint: "Shrink only if larger" },
  { value: "crop", label: "Crop", hint: "Crop to size" },
  { value: "pad", label: "Pad", hint: "Fit and pad empty space" },
] as const;

const FORMAT_OPTIONS: { value: Format; label: string; hint: string }[] = [
  { value: "webp", label: "WebP", hint: "Great compression, wide support" },
  { value: "avif", label: "AVIF", hint: "Smallest files, modern browsers" },
  { value: "jpeg", label: "JPEG", hint: "Maximum compatibility" },
  { value: "png", label: "PNG", hint: "Lossless, keeps transparency" },
];

const HOW_IT_WORKS_STEPS = [
  {
    icon: Upload,
    title: "Upload your image",
    description: "Drag and drop anywhere on the page, paste from your clipboard, or click to browse.",
  },
  {
    icon: Settings,
    title: "Dial in size and quality",
    description:
      "Pick a size preset, aspect ratio, format, and quality. Or set a target file size and let the tool find the best quality for you.",
  },
  {
    icon: Eye,
    title: "See the savings instantly",
    description:
      "The preview updates as you tweak settings and shows exactly how much smaller the file got. Flip to the original for a quick comparison.",
  },
  {
    icon: Download,
    title: "Download or copy the CDN URL",
    description: "Save the optimized file, or copy a CDN URL you can drop straight into your site's meta tags.",
  },
];

const RATIO_TOLERANCE = 0.02;
const PREVIEW_DEBOUNCE_MS = 280;

export default function ImageCompressorPage() {
  const [dragDepth, setDragDepth] = useState(0);
  const [fileName, setFileName] = useState("");
  const [originalSize, setOriginalSize] = useState(0);
  const [originalWidth, setOriginalWidth] = useState<number | null>(null);
  const [originalHeight, setOriginalHeight] = useState<number | null>(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewObjectUrl, setPreviewObjectUrl] = useState("");
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [compareTab, setCompareTab] = useState<"compressed" | "original">("compressed");
  const [format, setFormat] = useState<Format>("jpeg");
  const [width, setWidth] = useState<number>(DEFAULT_PRESET.width);
  const [height, setHeight] = useState<number>(DEFAULT_PRESET.height);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioValue>("original");
  const [presetName, setPresetName] = useState<string>("custom");
  const [fit, setFit] = useState("cover");
  const [quality, setQuality] = useState(82);
  const [targetKB, setTargetKB] = useState(400);
  const [autoBusy, setAutoBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const page = usePage<{ initial_blob_id?: string }>();
  const blobId = page.props.initial_blob_id;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewRequestId = useRef(0);
  const beginUploadRef = useRef<(f: File) => void>(() => {
    // Replaced with the real handler after render
  });

  const ratioLocked = aspectRatio !== "custom";
  const activeRatioNumber = resolveRatio(aspectRatio, originalWidth, originalHeight);
  const outputRatioLabel = formatRatioLabel(width, height);
  const activeRatioMeta = ASPECT_RATIOS.find((item) => item.value === aspectRatio);
  const busy = uploading || loadingPreview || autoBusy;
  const savings = originalSize && compressedSize != null ? 1 - compressedSize / originalSize : null;

  useEffect(() => {
    if (!blobId || width < 1 || height < 1) return;

    const requestId = ++previewRequestId.current;
    const timer = window.setTimeout(() => {
      void refreshPreview(requestId);
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
    // Intentionally sync preview with transform params / blob; refreshPreview closes over latest state.
  }, [blobId, format, width, height, fit, quality]);

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith("image/"));
      if (file) {
        e.preventDefault();
        beginUploadRef.current(file);
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  async function loadCompressedBlob(cdnUrl: string, requestId: number) {
    const response = await fetch(cdnUrl);
    const blob = await response.blob();
    if (requestId !== previewRequestId.current) return;
    setCompressedSize(blob.size);
    setPreviewObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
    setPreviewUrl(cdnUrl);
  }

  async function refreshPreview(requestId = ++previewRequestId.current) {
    if (!blobId) return;
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        blob_id: String(blobId),
        format,
        width: String(width),
        height: String(height),
        fit,
        quality: String(quality),
      });
      const res = await fetch(`/image-compressor/preview?${params.toString()}`);
      const json = (await res.json()) as { url: string };
      if (requestId !== previewRequestId.current) return;
      await loadCompressedBlob(json.url, requestId);
    } finally {
      if (requestId === previewRequestId.current) {
        setLoadingPreview(false);
      }
    }
  }

  async function handleAutoTarget(targetKilobytes = targetKB) {
    if (!blobId) return;
    setAutoBusy(true);
    setLoadingPreview(true);
    const requestId = ++previewRequestId.current;
    try {
      const params = new URLSearchParams({
        blob_id: String(blobId),
        target_bytes: String(targetKilobytes * 1024),
        format,
        width: String(width),
        height: String(height),
      });
      const res = await fetch(`/image-compressor/auto-target?${params.toString()}`);
      const json = (await res.json()) as { quality?: number; url?: string };
      if (json.quality) setQuality(Number(json.quality));
      if (json.url) await loadCompressedBlob(json.url, requestId);
    } finally {
      setAutoBusy(false);
      if (requestId === previewRequestId.current) {
        setLoadingPreview(false);
      }
    }
  }

  function applyDimensions(nextWidth: number, nextHeight: number, nextAspect?: AspectRatioValue) {
    const safeWidth = Math.max(1, Math.round(nextWidth) || 1);
    const safeHeight = Math.max(1, Math.round(nextHeight) || 1);
    setWidth(safeWidth);
    setHeight(safeHeight);

    const matchedPreset = findMatchingPreset(safeWidth, safeHeight);
    setPresetName(matchedPreset?.name ?? "custom");

    if (nextAspect) {
      setAspectRatio(nextAspect);
      return;
    }

    setAspectRatio(detectAspectRatio(safeWidth, safeHeight, originalWidth, originalHeight));
  }

  function handleResolutionChange(value: string) {
    if (!value || value === "custom") {
      setPresetName("custom");
      return;
    }

    const preset = RESOLUTION_PRESETS.find((item) => item.name === value);
    if (!preset) return;

    const nextAspect = detectAspectRatio(preset.width, preset.height, originalWidth, originalHeight);
    applyDimensions(preset.width, preset.height, nextAspect === "original" ? "custom" : nextAspect);
    setPresetName(preset.name);
  }

  function handleAspectRatioChange(value: string) {
    // Radix clears single toggles when re-clicked; keep the current ratio.
    if (!value) return;

    const next = value as AspectRatioValue;
    if (next === "custom") {
      setAspectRatio("custom");
      setPresetName(findMatchingPreset(width, height)?.name ?? "custom");
      return;
    }

    if (next === "original") {
      if (!originalWidth || !originalHeight) {
        // Remember the intent; apply when the source image loads.
        setAspectRatio("original");
        setPresetName(findMatchingPreset(width, height)?.name ?? "custom");
        return;
      }
      const ratio = originalWidth / originalHeight;
      applyDimensions(width, width / ratio, "original");
      return;
    }

    const ratio = resolveRatio(next, originalWidth, originalHeight);
    if (!ratio) return;

    applyDimensions(width, width / ratio, next);
  }

  function handleWidthChange(raw: string) {
    const nextWidth = Number(raw);
    if (!Number.isFinite(nextWidth) || nextWidth <= 0) {
      setWidth(nextWidth);
      return;
    }

    if (ratioLocked && activeRatioNumber) {
      applyDimensions(nextWidth, nextWidth / activeRatioNumber, aspectRatio);
      return;
    }

    // Keep "Original" selected before the source loads (ratio applies on upload).
    applyDimensions(nextWidth, height, aspectRatio === "original" ? "original" : undefined);
  }

  function handleHeightChange(raw: string) {
    const nextHeight = Number(raw);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      setHeight(nextHeight);
      return;
    }

    if (ratioLocked && activeRatioNumber) {
      applyDimensions(nextHeight * activeRatioNumber, nextHeight, aspectRatio);
      return;
    }

    applyDimensions(width, nextHeight, aspectRatio === "original" ? "original" : undefined);
  }

  function unlockRatio() {
    setAspectRatio("custom");
  }

  function useSourceSize() {
    if (!originalWidth || !originalHeight) return;
    applyDimensions(originalWidth, originalHeight, "original");
  }

  const usingSourceSize = !!originalWidth && !!originalHeight && width === originalWidth && height === originalHeight;

  function triggerFileDialog() {
    fileInputRef.current?.click();
  }

  function beginUpload(f: File) {
    if (uploading) return;
    if (!f.type.startsWith("image/")) return;

    setFileName(f.name || "pasted-image");
    setOriginalSize(f.size);

    // Keep current output size/ratio/quality; only the source image changes.
    setUploading(true);
    setUploadProgress(0);
    setLoadingPreview(true);
    setCompressedSize(null);
    setCompareTab("compressed");

    const url = URL.createObjectURL(f);
    setSourceUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });

    const img = new Image();
    img.onload = () => {
      setOriginalWidth(img.width);
      setOriginalHeight(img.height);
      // If "Original" is selected, apply the new source ratio to the current width.
      if (aspectRatio === "original") {
        const ratio = img.width / img.height;
        applyDimensions(width, width / ratio, "original");
      }
    };
    img.src = url;

    router.post(
      "/image-compressor",
      { file: f },
      {
        forceFormData: true,
        preserveScroll: true,
        onProgress: (event) => {
          if (typeof event?.percentage === "number") {
            setUploadProgress(event.percentage);
          }
        },
        onFinish: () => {
          setUploading(false);
          setUploadProgress(null);
          setLoadingPreview(false);
        },
        onError: () => {
          setUploading(false);
          setUploadProgress(null);
          setLoadingPreview(false);
        },
      },
    );
  }
  beginUploadRef.current = beginUpload;

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) beginUpload(f);
    // Allow re-selecting the same file.
    e.target.value = "";
  }

  function onPageDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragDepth(0);
    if (uploading) return;
    const f = e.dataTransfer.files?.[0];
    if (f) beginUpload(f);
  }

  function handleDownload() {
    if (!previewObjectUrl) return;
    const a = document.createElement("a");
    a.href = previewObjectUrl;
    a.download = makeDownloadName(fileName || "image", format);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleCopyUrl() {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  }

  const sizeSummary =
    presetName !== "custom"
      ? `${width} × ${height} · ${outputRatioLabel} · ${presetName}`
      : `${width} × ${height} · ${outputRatioLabel}`;

  const hasImage = !!fileName || !!blobId;
  const dragActive = dragDepth > 0;

  return (
    <>
      <Head title="Compress Image to 100KB, 200KB or Any Size - Free Image Compressor" />
      <div
        className="relative min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
        onDragEnter={(e) => {
          if (e.dataTransfer.types.includes("Files")) setDragDepth((d) => d + 1);
        }}
        onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onPageDrop}
      >
        {dragActive && (
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-orange-500/10 backdrop-blur-[2px]">
            <div className="rounded-2xl border-2 border-dashed border-orange-500 bg-white/95 px-10 py-8 text-center shadow-xl dark:bg-gray-900/95">
              <ImagePlus className="mx-auto h-10 w-10 text-orange-500" />
              <p className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Drop to compress</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">AVIF, PNG, JPEG, or WebP</p>
            </div>
          </div>
        )}

        <PublicHeader />

        <div className="container mx-auto max-w-6xl px-4 py-8 md:py-12">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">Image Compressor</h1>
            <p className="text-muted-foreground mt-4 max-w-2xl text-base md:text-lg">
              Compress JPEG, PNG, WebP, and AVIF images to 100 KB, 200 KB, 1 MB, or any target size without losing
              quality. See the savings, then grab the file or a CDN link.
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/avif, image/png, image/jpeg, image/webp"
            onChange={onInputChange}
            className="hidden"
          />

          <div className="mt-8 md:mt-12">
            {!hasImage ? (
              <button
                type="button"
                onClick={triggerFileDialog}
                disabled={uploading}
                className="mx-auto mb-8 block w-full max-w-2xl cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 p-10 text-center transition-colors hover:border-orange-400 hover:bg-orange-50/40 dark:border-gray-700 dark:hover:border-orange-500 dark:hover:bg-orange-900/10"
              >
                <ImagePlus className="mx-auto h-10 w-10 text-orange-500" />
                <p className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
                  Drop an image anywhere, or click to browse
                </p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  You can also paste from your clipboard with <Kbd>⌘V</Kbd>
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  AVIF, PNG, JPEG, and WebP file types are supported.
                </p>
              </button>
            ) : (
              <div className="mx-auto mb-8 max-w-3xl rounded-2xl border bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-4">
                  {sourceUrl ? (
                    <img
                      src={sourceUrl}
                      alt="Source"
                      className="h-14 w-14 shrink-0 rounded-lg border object-cover dark:border-gray-600"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-gray-50 dark:border-gray-600 dark:bg-gray-700">
                      <ImagePlus className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                      {fileName || "Uploaded image"}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                      {originalWidth && originalHeight && (
                        <span>
                          {originalWidth} × {originalHeight}
                        </span>
                      )}
                      {originalSize > 0 && <span>· {readableSize(originalSize)}</span>}
                      {uploading && uploadProgress != null && <span>· Uploading {uploadProgress}%…</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {originalWidth && originalHeight && !usingSourceSize && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="hidden sm:inline-flex"
                        onClick={useSourceSize}
                      >
                        Use source size
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={triggerFileDialog} disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Replace"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <section className="grid gap-6 md:gap-8 lg:grid-cols-2">
              <div className="rounded-2xl border bg-white p-4 shadow-lg sm:p-6 dark:border-gray-700 dark:bg-gray-800">
                <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">Compression Settings</h2>

                <div className="mb-5 rounded-lg border border-orange-200/80 bg-orange-50/70 px-3 py-3 dark:border-orange-900/50 dark:bg-orange-950/30">
                  <div className="flex items-start gap-2">
                    {ratioLocked ? (
                      <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" />
                    ) : (
                      <Link2Off className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    )}
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{sizeSummary}</p>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        {aspectRatio === "original" && !originalWidth ? (
                          <>Original ratio selected. Upload an image and the height will match its proportions.</>
                        ) : ratioLocked ? (
                          <>
                            Ratio locked to {activeRatioMeta?.label ?? outputRatioLabel}
                            {activeRatioMeta?.hint ? ` (${activeRatioMeta.hint})` : ""}. Change one side and the other
                            follows.
                          </>
                        ) : (
                          <>Width and height move independently. Pick a ratio above to link them again.</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <Label htmlFor="resolution-preset">Resolution preset</Label>
                    <Select value={presetName} onValueChange={handleResolutionChange}>
                      <SelectTrigger id="resolution-preset" className="mt-1">
                        <SelectValue placeholder="Select a common resolution..." />
                      </SelectTrigger>
                      <SelectContent>
                        {RESOLUTION_PRESETS.map((preset) => (
                          <SelectItem key={preset.name} value={preset.name}>
                            {preset.name} ({preset.width}×{preset.height})
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Custom size</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <Label>Aspect ratio</Label>
                      {ratioLocked && (
                        <button
                          type="button"
                          onClick={unlockRatio}
                          className="text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400"
                        >
                          Unlock to custom
                        </button>
                      )}
                    </div>
                    <ToggleGroup
                      type="single"
                      value={aspectRatio}
                      onValueChange={handleAspectRatioChange}
                      className="mt-2 flex w-full flex-wrap justify-start gap-1"
                    >
                      {ASPECT_RATIOS.map(({ value, label, hint }) => (
                        <ToggleGroupItem
                          key={value}
                          value={value}
                          aria-label={label}
                          className="flex-none rounded-md border px-2.5 shadow-none data-[state=on]:border-orange-500 data-[state=on]:bg-orange-50 data-[state=on]:text-orange-700 dark:data-[state=on]:border-orange-400 dark:data-[state=on]:bg-orange-950/40 dark:data-[state=on]:text-orange-300"
                          title={hint}
                        >
                          {label}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    {aspectRatio === "original" && originalWidth && originalHeight && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Source ratio is {formatRatioLabel(originalWidth, originalHeight)} ({originalWidth}×
                        {originalHeight}).
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                      <Label htmlFor="width">Width (px)</Label>
                      <Input
                        id="width"
                        type="number"
                        min={1}
                        value={width}
                        onChange={(e) => handleWidthChange(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="height">Height (px)</Label>
                      <Input
                        id="height"
                        type="number"
                        min={1}
                        value={height}
                        onChange={(e) => handleHeightChange(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="flex-1">
                      <Label htmlFor="format">Format</Label>
                      <select
                        id="format"
                        className="mt-1 w-full rounded-md border bg-transparent p-2 text-sm dark:border-gray-600"
                        value={format}
                        onChange={(e) => setFormat(e.target.value as Format)}
                      >
                        {FORMAT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {FORMAT_OPTIONS.find((o) => o.value === format)?.hint}
                      </p>
                    </div>
                    <div className="flex-1">
                      <Label htmlFor="fit">Fit</Label>
                      <select
                        id="fit"
                        className="mt-1 w-full rounded-md border bg-transparent p-2 text-sm dark:border-gray-600"
                        value={fit}
                        onChange={(e) => setFit(e.target.value)}
                      >
                        {FIT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {FIT_OPTIONS.find((o) => o.value === fit)?.hint}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="quality">Quality</Label>
                      <span className="text-xs font-medium text-gray-700 tabular-nums dark:text-gray-300">
                        {quality}%
                      </span>
                    </div>
                    <Slider
                      id="quality"
                      min={10}
                      max={100}
                      step={1}
                      value={[quality]}
                      onValueChange={([value]) => setQuality(value)}
                      className="mt-3"
                    />
                    <div className="mt-1.5 flex justify-between text-[11px] text-gray-400 dark:text-gray-500">
                      <span>Smaller file</span>
                      <span>Higher quality</span>
                    </div>
                    {format === "png" && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        PNG is lossless, so quality has little effect. Try WebP or AVIF for smaller files.
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-dashed p-3 dark:border-gray-600">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="target" className="whitespace-nowrap">
                          Auto target size
                        </Label>
                        <Input
                          id="target"
                          type="number"
                          value={targetKB}
                          onChange={(e) => setTargetKB(Number(e.target.value))}
                          className="w-20 text-right"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">KB</span>
                      </div>
                      <Button
                        disabled={!blobId || busy}
                        onClick={() => void handleAutoTarget()}
                        className="w-full bg-orange-500 hover:bg-orange-600 sm:w-auto"
                      >
                        {autoBusy ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Optimizing…
                          </>
                        ) : (
                          "Find quality"
                        )}
                      </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {[50, 100, 200, 500, 1024].map((kb) => (
                        <button
                          key={kb}
                          type="button"
                          disabled={!blobId || busy}
                          onClick={() => {
                            setTargetKB(kb);
                            void handleAutoTarget(kb);
                          }}
                          className="rounded-full border px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-orange-400 hover:text-orange-600 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:border-orange-500 dark:hover:text-orange-400"
                        >
                          {kb === 1024 ? "1 MB" : `${kb} KB`}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Finds the highest quality that keeps the file under your target size.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-4 shadow-lg sm:p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Result</h2>
                  <div className="flex items-center gap-2">
                    <Tabs value={compareTab} onValueChange={(v) => setCompareTab(v as typeof compareTab)}>
                      <TabsList>
                        <TabsTrigger value="compressed">Compressed</TabsTrigger>
                        <TabsTrigger value="original" disabled={!sourceUrl}>
                          Original
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => void refreshPreview()}
                      disabled={!blobId || busy}
                      title="Refresh preview"
                    >
                      <RefreshCcw className={`h-4 w-4 ${loadingPreview ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                </div>

                <div
                  className="relative overflow-hidden rounded-lg border dark:border-gray-600"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%), linear-gradient(45deg, rgba(0,0,0,0.05) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.05) 75%)",
                    backgroundSize: "16px 16px",
                    backgroundPosition: "0 0, 8px 8px",
                  }}
                >
                  {compareTab === "original" && sourceUrl ? (
                    <img src={sourceUrl} alt="Original" className="mx-auto max-h-96 object-contain" />
                  ) : previewObjectUrl ? (
                    <>
                      {loadingPreview && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/50">
                          <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow dark:bg-gray-800 dark:text-gray-300">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Updating…
                          </span>
                        </div>
                      )}
                      <img
                        src={previewObjectUrl}
                        alt="Compressed preview"
                        className="mx-auto max-h-96 object-contain"
                      />
                    </>
                  ) : busy ? (
                    <Skeleton className="h-64 w-full rounded-none" />
                  ) : (
                    <div className="flex h-64 items-center justify-center bg-white text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      {hasImage ? "Preparing your preview…" : "Upload an image to see the result"}
                    </div>
                  )}
                </div>

                {compressedSize != null && (
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-lg border bg-gray-50 px-4 py-3 text-sm dark:border-gray-600 dark:bg-gray-900/40">
                    {originalSize > 0 && (
                      <>
                        <span className="text-gray-500 dark:text-gray-400">{readableSize(originalSize)}</span>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                      </>
                    )}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {readableSize(compressedSize)}
                    </span>
                    {savings != null &&
                      (savings >= 0 ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300">
                          −{Math.round(savings * 100)}% smaller
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/50 dark:text-amber-300">
                          +{Math.round(-savings * 100)}% larger
                        </Badge>
                      ))}
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {width}×{height} {format.toUpperCase()}
                    </span>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => void handleCopyUrl()}
                    disabled={!previewUrl || busy}
                    className="flex-1"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copied!" : "Copy CDN URL"}
                  </Button>
                  <Button
                    onClick={handleDownload}
                    disabled={!previewObjectUrl || busy}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </div>

        <section className="container mx-auto max-w-6xl px-4 py-12 md:py-16">
          <div className="mb-8 text-center">
            <h2 className="mb-3 text-3xl font-bold text-gray-900 dark:text-white">How it works</h2>
            <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
              From upload to CDN-ready image in four quick steps.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS_STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-lg transition-transform hover:-translate-y-1 dark:bg-gray-800 dark:shadow-none"
                >
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <FinalCta />
      </div>
      <SiteFooter />
    </>
  );
}

function resolveRatio(
  value: AspectRatioValue,
  originalWidth: number | null,
  originalHeight: number | null,
): number | null {
  switch (value) {
    case "original":
      if (originalWidth && originalHeight) return originalWidth / originalHeight;
      return null;
    case "4:3":
      return 4 / 3;
    case "3:2":
      return 3 / 2;
    case "16:9":
      return 16 / 9;
    case "1:1":
      return 1;
    case "1.905:1":
      return 1.905;
    case "custom":
      return null;
    default:
      return null;
  }
}

function ratiosMatch(a: number, b: number) {
  return Math.abs(a - b) / b <= RATIO_TOLERANCE;
}

function detectAspectRatio(
  width: number,
  height: number,
  originalWidth: number | null,
  originalHeight: number | null,
): AspectRatioValue {
  if (!width || !height) return "custom";
  const current = width / height;

  const candidates: { value: AspectRatioValue; ratio: number }[] = [
    { value: "1.905:1", ratio: 1.905 },
    { value: "16:9", ratio: 16 / 9 },
    { value: "4:3", ratio: 4 / 3 },
    { value: "3:2", ratio: 3 / 2 },
    { value: "1:1", ratio: 1 },
  ];

  if (originalWidth && originalHeight) {
    candidates.unshift({ value: "original", ratio: originalWidth / originalHeight });
  }

  for (const candidate of candidates) {
    if (ratiosMatch(current, candidate.ratio)) return candidate.value;
  }

  return "custom";
}

function findMatchingPreset(width: number, height: number) {
  return RESOLUTION_PRESETS.find((preset) => preset.width === width && preset.height === height) ?? null;
}

function formatRatioLabel(width: number, height: number) {
  if (!width || !height) return "custom";
  const value = width / height;
  if (ratiosMatch(value, 1)) return "1:1";
  if (ratiosMatch(value, 16 / 9)) return "16:9";
  if (ratiosMatch(value, 4 / 3)) return "4:3";
  if (ratiosMatch(value, 3 / 2)) return "3:2";
  if (ratiosMatch(value, 1.905)) return "1.91:1";
  return `${value.toFixed(2)}:1`;
}

function readableSize(bytes: number) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${units[i]}`;
}

function makeDownloadName(base: string, format: string) {
  const stem = base.replace(/\.[a-z0-9]+$/i, "");
  const ext = format === "jpeg" ? "jpg" : format;
  return `${stem}-compressed.${ext}`;
}
